(function () {
  "use strict";

  if (window.MarineMaxCoveoFilterMutations) return;

  function setCoveoUiMutationBusy(container, busy) {
    if (!container || container.nodeType !== 1) return;
    container.dataset.coveoUiMutationBusy = busy ? "true" : "false";
    const targets = container.querySelectorAll(
      "[data-coveo-chip-remove], [data-coveo-clear-all-filters], #coveo-reset-filters, #coveo-clear-filters",
    );
    targets.forEach(function (element) {
      const isRemovableChip = element.matches?.("[data-coveo-chip-remove]");
      if (busy) {
        if (!isRemovableChip) {
          element.setAttribute("aria-busy", "true");
        }
        if (!isRemovableChip && "disabled" in element) {
          element.disabled = true;
        }
        return;
      }

      element.removeAttribute("aria-busy");
      if ("disabled" in element) {
        element.disabled = false;
      }
    });
  }

  function beginCoveoUiMutation(container, durationMs) {
    if (!container || container.nodeType !== 1) {
      return function () { };
    }

    const ttl = Math.max(250, Number(durationMs) || 700);
    const token = Number(container._coveoUiMutationToken || 0) + 1;
    const until = Date.now() + ttl;
    container._coveoUiMutationToken = token;
    container._coveoUiMutationInFlight = true;
    container._coveoSuppressProductRenderUntil = Math.max(
      Number(container._coveoSuppressProductRenderUntil || 0),
      until,
    );
    setCoveoUiMutationBusy(container, true);

    let released = false;
    const release = function () {
      if (released || container._coveoUiMutationToken !== token) return;
      released = true;
      container._coveoUiMutationInFlight = false;
      setCoveoUiMutationBusy(container, false);
    };
    const watchdogTimer = window.setTimeout(release, ttl);

    return function endCoveoUiMutation() {
      window.clearTimeout(watchdogTimer);
      release();
    };
  }

  function createCoveoUiMutationLease(container) {
    let current = true;
    let fallbackAborted = false;
    const controller =
      typeof AbortController === "function" ? new AbortController() : null;
    const signal = controller
      ? controller.signal
      : Object.freeze({
          get aborted() {
            return fallbackAborted;
          },
        });
    let record = null;
    const lease = Object.freeze({
      signal,
      isCurrent() {
        return !!(
          current &&
          !signal.aborted &&
          container._coveoUiMutationLeaseRecord === record
        );
      },
    });

    record = {
      cancel(reason) {
        if (!current) return;
        current = false;
        fallbackAborted = true;
        if (controller) {
          try {
            controller.abort(reason);
          } catch (_) {
            controller.abort();
          }
        }
      },
      finish() {
        current = false;
        if (container._coveoUiMutationLeaseRecord === record) {
          delete container._coveoUiMutationLeaseRecord;
        }
      },
      lease,
    };
    return record;
  }

  function startCoveoUiMutationLease(container) {
    const previous = container._coveoUiMutationLeaseRecord;
    if (previous && typeof previous.cancel === "function") {
      previous.cancel("superseded");
    }
    const record = createCoveoUiMutationLease(container);
    container._coveoUiMutationLeaseRecord = record;
    return record;
  }

  function cancelCoveoUiMutations(container, reason) {
    if (!container || container.nodeType !== 1) return;
    container._coveoUiMutationGeneration =
      Number(container._coveoUiMutationGeneration || 0) + 1;
    const active = container._coveoUiMutationLeaseRecord;
    if (active && typeof active.cancel === "function") {
      active.cancel(reason || "cancelled");
    }
    if (container._coveoUiMutationLeaseRecord === active) {
      delete container._coveoUiMutationLeaseRecord;
    }
    container._coveoUiMutationToken =
      Number(container._coveoUiMutationToken || 0) + 1;
    container._coveoUiMutationInFlight = false;
    setCoveoUiMutationBusy(container, false);
  }

  function enqueueCoveoUiMutation(container, task, timeoutMs) {
    if (!container || container.nodeType !== 1 || typeof task !== "function") {
      return Promise.resolve();
    }

    const maxWait = Math.max(1000, Number(timeoutMs) || 5000);
    const generation = Number(container._coveoUiMutationGeneration || 0);
    const previous = container._coveoUiMutationQueue || Promise.resolve();
    const queued = Promise.resolve(previous)
      .catch(function () { })
      .then(async function () {
        if (
          generation !== Number(container._coveoUiMutationGeneration || 0)
        ) {
          return;
        }
        const leaseRecord = startCoveoUiMutationLease(container);
        let timeoutTimer = null;
        const timedOut = Symbol("coveo-ui-mutation-timeout");
        const taskPromise = Promise.resolve()
          .then(function () {
            return task(leaseRecord.lease);
          })
          .catch(function (error) {
            console.warn("[Coveo Filters] UI mutation failed:", error);
          })
          .finally(function () {
            leaseRecord.finish();
          });
        const result = await Promise.race([
          taskPromise,
          new Promise(function (resolve) {
            timeoutTimer = window.setTimeout(function () {
              resolve(timedOut);
            }, maxWait);
          }),
        ]);
        if (timeoutTimer != null) window.clearTimeout(timeoutTimer);
        if (result === timedOut) {
          console.warn("[Coveo Filters] UI mutation timed out; continuing queue");
        }
      });

    let queueTail = null;
    queueTail = queued.finally(function () {
      if (container._coveoUiMutationQueue === queueTail) {
        delete container._coveoUiMutationQueue;
      }
    });
    container._coveoUiMutationQueue = queueTail;
    return queueTail;
  }

  Object.defineProperty(window, "MarineMaxCoveoFilterMutations", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({
      beginCoveoUiMutation,
      cancelCoveoUiMutations,
      enqueueCoveoUiMutation,
    }),
    writable: false,
  });
})();
